import { Component, OnInit, inject, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ApiService } from '../../core/services/api.service';
import { Transaction, TransactionListFilters } from '../../core/models/api.models';

@Component({
  selector: 'app-transactions',
  standalone: true,
  imports: [DatePipe, FormsModule, RouterLink],
  templateUrl: './transactions.component.html',
  styleUrl: './transactions.component.scss',
})
export class TransactionsComponent implements OnInit {
  private readonly api = inject(ApiService);

  readonly loading = signal(false);
  readonly syncing = signal(false);
  readonly error = signal<string | null>(null);
  readonly success = signal<string | null>(null);
  readonly rows = signal<Transaction[]>([]);
  readonly page = signal(1);
  readonly limit = signal(20);
  readonly total = signal(0);

  email = '';
  userId = '';
  senderUserId = '';
  receiver = '';
  q = '';
  type = '';
  status = '';
  leg = '';
  toPartyType = '';

  ngOnInit(): void {
    this.load();
  }

  private filters(page = this.page()): TransactionListFilters {
    return {
      page,
      limit: this.limit(),
      email: this.email.trim() || undefined,
      user_id: this.userId.trim() || undefined,
      sender_user_id: this.senderUserId.trim() || undefined,
      receiver: this.receiver.trim() || undefined,
      q: this.q.trim() || undefined,
      type: this.type || undefined,
      status: this.status || undefined,
      leg: this.leg || undefined,
      to_party_type: this.toPartyType || undefined,
    };
  }

  load(page = this.page()): void {
    this.loading.set(true);
    this.error.set(null);
    this.api.getTransactions(this.filters(page)).subscribe({
      next: (data) => {
        this.rows.set(data.rows || []);
        this.page.set(data.page);
        this.limit.set(data.limit);
        this.total.set(data.total);
        this.loading.set(false);
      },
      error: (err: { message?: string }) => {
        this.error.set(err?.message || 'Failed to load transactions');
        this.loading.set(false);
      },
    });
  }

  applyFilters(): void {
    this.load(1);
  }

  sync(): void {
    if (this.syncing()) return;
    this.syncing.set(true);
    this.error.set(null);
    this.success.set(null);
    this.api.syncTransactions({}).subscribe({
      next: (data) => {
        this.syncing.set(false);
        const s = data?.summary;
        this.success.set(
          s
            ? `Synced from Kobble: ${s.fetched} fetched, ${s.created} created, ${s.updated} updated` +
              (s.skipped ? `, ${s.skipped} skipped` : '') +
              (s.errors ? `, ${s.errors} errors` : '')
            : 'Transactions synced from Kobble'
        );
        this.load(1);
      },
      error: (err: { message?: string }) => {
        this.syncing.set(false);
        this.error.set(err?.message || 'Failed to sync transactions from Kobble');
      },
    });
  }

  clearFilters(): void {
    this.email = '';
    this.userId = '';
    this.senderUserId = '';
    this.receiver = '';
    this.q = '';
    this.type = '';
    this.status = '';
    this.leg = '';
    this.toPartyType = '';
    this.load(1);
  }

  userLabel(row: Transaction): string {
    const u = row.user;
    if (!u) return row.user_id != null ? String(row.user_id) : '—';
    const name = [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
    if (name && u.email) return `${name} (${u.email})`;
    return u.email || name || String(u.id ?? row.user_id ?? '—');
  }

  receiverLabel(row: Transaction): string {
    if (row.counterparty_name) return row.counterparty_name;
    if (row.to_party_type === 'merchant_wallet') {
      return row.merchant_kobble_wallet_id || row.to_kobble_wallet_id || 'Merchant wallet';
    }
    if (row.beneficiary?.name) return row.beneficiary.name;
    if (row.to_kobble_wallet_id) return row.to_kobble_wallet_id;
    return '—';
  }

  totalPages(): number {
    return Math.max(1, Math.ceil(this.total() / this.limit()));
  }

  prev(): void {
    if (this.page() > 1) this.load(this.page() - 1);
  }

  next(): void {
    if (this.page() < this.totalPages()) this.load(this.page() + 1);
  }
}
